import { useState } from 'react';
import { Brain, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';

interface ThinkingBlockProps {
  content: string;
  isComplete?: boolean;
  defaultExpanded?: boolean;
  className?: string;
}

export function ThinkingBlock({
  content,
  isComplete = false,
  defaultExpanded = false,
  className
}: ThinkingBlockProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Don't render if no content
  if (!content.trim()) return null;

  return (
    <div className={cn(
      'rounded-lg border overflow-hidden',
      'border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20',
      className
    )}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-left',
          'hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors'
        )}
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-purple-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-purple-500" />
        )}
        <Brain className="w-4 h-4 text-purple-500" />
        <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
          AI Thinking
        </span>
        {!isComplete && (
          <span className="ml-2 flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse" />
            <span className="text-xs text-purple-500">thinking...</span>
          </span>
        )}
      </button>

      {isExpanded && (
        <div className="px-4 py-3 border-t border-purple-200 dark:border-purple-800">
          <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">
            {content}
          </pre>
        </div>
      )}
    </div>
  );
}
